// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {type CoretChannelAccess, purchaseCoretChannelAccess} from '@app/features/coret/CoretMediaApi';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {CoinsIcon, PhoneCallIcon} from '@phosphor-icons/react';
import {useState} from 'react';

export function CoretChannelAccessModal({
	channelName,
	channelId,
	access,
	onGranted,
}: {
	channelName: string;
	channelId: string;
	access: CoretChannelAccess;
	onGranted: () => void;
}) {
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const fee = access.fee;
	if (!fee) return null;
	const canAfford = access.balance >= fee.price;

	const handlePurchase = async () => {
		setSubmitting(true);
		setError(null);
		try {
			await purchaseCoretChannelAccess(channelId);
			ModalCommands.pop();
			onGranted();
		} catch (reason) {
			setError(reason instanceof Error ? reason.message : 'Não foi possível liberar a entrada nesta chamada.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal.Root size="small" centered>
			<Modal.Header title="Chamada paga" icon={<PhoneCallIcon weight="fill" />} />
			<Modal.Content>
				<Modal.ContentLayout>
					<Modal.Description>
						A entrada em <strong>{channelName}</strong> custa <strong>{fee.price} Coins</strong>.
					</Modal.Description>
					<p>
						<CoinsIcon weight="fill" aria-hidden="true" /> Saldo atual: <strong>{access.balance} Coins</strong>
					</p>
					<p>
						{fee.mode === 'per_entry'
							? 'A cobrança vale para esta entrada. Ao sair e entrar novamente, uma nova cobrança será solicitada.'
							: 'O pagamento libera o acesso permanente a este canal.'}
					</p>
					{!canAfford ? <p role="alert">Saldo insuficiente para entrar nesta chamada.</p> : null}
					{error ? <p role="alert">{error}</p> : null}
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={() => ModalCommands.pop()} disabled={submitting}>
					Cancelar
				</Button>
				<Button onClick={() => void handlePurchase()} disabled={!canAfford || submitting} submitting={submitting}>
					<CoinsIcon weight="fill" /> Pagar e entrar
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
}
